import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState } from 'react';
import { Avatar, Box, Button, Chip, Divider, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { Global_Product_Family_Enum, Product_Family_Name_Enum, User_Role_Enum } from '@kheops/graphql';
import { ProductFamilyMap, productFamilyMap } from '@kheops/utils';
import Sheet from '../../common/components/Sheet';
import { ProductFamilyTypeIntl, PackagingFamilyTypeIntl } from '../../common/i18n/product-family.translation';
import PhotosUtils from '../../common/utils/photos.utils';
import ProductFamilyChip from '../../user/settingsDialog/ProductFamilyChip';
import { ellipsisStyle } from '../../common/utils/style.utils';
import { userAtom } from '../../state';
import { UserRowData } from './CompanyTeamPage';
import UserRoleSelect from './UserRoleSelect';
import CompanyAdminSwitch from './CompanyAdminSwitch';
import DeleteUserButton from './DeleteUserButton';

export interface CompanyTeamUserDetailProps {
  open: boolean;
  close: (newRole?: User_Role_Enum, isAdmin?: boolean) => void;
  loading: boolean;
  data: UserRowData;
}

export default function CompanyTeamUserDetail({ open, close, loading, data: { user, role, isAdmin, productFamilies } }: CompanyTeamUserDetailProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'company', 'settings']);
  const { id: userId } = useAtomValue(userAtom);
  const [currentRole, setCurrentRole] = useState(role);
  const [currentAdminStatus, setCurrentAdminStatus] = useState(isAdmin);

  const roleUpdated = isAdmin !== currentAdminStatus || role !== currentRole;

  const subFamiliesByFamilies = useMemo(() => {
    if (productFamilies[0]?.global_setting) {
      return;
    }

    const result = {
      [Product_Family_Name_Enum.Beverages]: [],
      [Product_Family_Name_Enum.FreshFood]: [],
      [Product_Family_Name_Enum.Grocery]: [],
      [Product_Family_Name_Enum.NonFoodProducts]: [],
    } as ProductFamilyMap;

    productFamilies.forEach(({ product_family, product_sub_family }) => {
      if (product_family) {
        result[product_family] = productFamilyMap[product_family];
      } else if (product_sub_family) {
        const productFamily = Object.entries(productFamilyMap).find(([, subFamilies]) => subFamilies.includes(product_sub_family))![0] as Product_Family_Name_Enum;

        result[productFamily].push(product_sub_family);
      }
    });

    return result;
  }, [productFamilies]);

  const updateRole = (): void => {
    close(currentRole, currentAdminStatus);
  };

  return (
    <Sheet open={open} onClose={() => close()} width={325} title={t('common:details')}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
          <Avatar
            size={80}
            src={PhotosUtils.getUserAvatarPicture(user)}
            variant="square"
            sx={{
              borderRadius: 6,
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Typography variant="bodyLarge" sx={{ ...ellipsisStyle }}>
              {user.first_name} {user.last_name}
            </Typography>
            <Typography variant="bodyMedium" color="onSurfaceVariant" sx={{ ...ellipsisStyle }}>
              {user.email}
            </Typography>
          </Box>
        </Box>
        <UserRoleSelect onChange={setCurrentRole} value={currentRole} />
        <Divider />
        {role === User_Role_Enum.BuyerManager && !!productFamilies.length && (
          <>
            <Box>
              {productFamilies[0].global_setting
                ? (
                  <Chip
                    variant="tag"
                    color="surfaceContainerHigh"
                    label={t(productFamilies[0].global_setting === Global_Product_Family_Enum.All ? 'settings:all_departments' : 'settings:no_departments')}
                  />
                ) : (
                  Object.entries(subFamiliesByFamilies!)
                    .filter(([, subFamilies]) => !!subFamilies.length)
                    .map(([family, subFamilies]) => (
                      <Box key={family} sx={{ p: 1.5 }}>
                        <Typography variant="bodyMedium" sx={{ mb: 1 }}>
                          {t(`product-family:${ProductFamilyTypeIntl[family as Product_Family_Name_Enum]}`)}
                        </Typography>
                        {subFamilies.length === productFamilyMap[family as Product_Family_Name_Enum].length
                          ? (
                            <Chip variant="tag" color="surfaceContainerHigh" label={t('settings:all_departments')} />
                          ) : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {
                                subFamilies.map((subFamily) => (
                                  <ProductFamilyChip
                                    variant="tag"
                                    subFamily={subFamily}
                                    color="surfaceContainerHighest"
                                    label={t(`product-family:${[PackagingFamilyTypeIntl[subFamily]]}`)}
                                    key={subFamily}
                                  />
                                ))
                              }
                            </Box>
                          )}
                      </Box>
                    ))
                )}
            </Box>
            <Divider />
          </>
        )}
        <CompanyAdminSwitch value={currentAdminStatus} onChange={setCurrentAdminStatus} />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            justifyContent: 'flex-end',
          }}
        >
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            {user.id !== userId && (
              <DeleteUserButton userId={user.id} fullName={`${user.first_name} ${user.last_name}`} role={role} isAdmin={!!isAdmin} />
            )}
            <Button
              variant="contained"
              onClick={updateRole}
              disabled={!roleUpdated}
              loading={loading}
              startIcon={<EditIcon />}
            >
              {t('common:update')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Sheet>
  );
}
